import classNames from 'classnames';
import React, { useState } from 'react';
import {
  PrivacyPolicyContent as PrivacyPolicyContentEntry,
  PrivacyPolicyDocument,
  PrivacyPolicySection,
  PrivacyPolicySubsection,
} from '../../../types/privacy-policy-content';
import { SectionTilesTypesProps } from '../../../utils/SectionProps';
import Button from '../../elements/Button';

interface PrivacyPolicyProps extends SectionTilesTypesProps {
  className?: string;
  policyContent: PrivacyPolicyContentEntry[];
}

const defaultProps: PrivacyPolicyProps = {
  topOuterDivider: false,
  bottomOuterDivider: false,
  topDivider: false,
  bottomDivider: false,
  hasBgColor: false,
  invertColor: false,
  pushLeft: false,
  policyContent: [],
};

const normalizePolicyHtml = (markup: string) =>
  markup.replace(/<ul>\s*<h4>(.*?)<\/h4>\s*<\/ul>/gs, '<h4>$1</h4>').replace(/<p>\s*<\/p>/g, '');

const renderPolicyBody = (bodyHtml: string, className: string) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: normalizePolicyHtml(bodyHtml) }}></div>
);

const normalizeDocument = (value: Partial<PrivacyPolicyDocument> | undefined): PrivacyPolicyDocument => ({
  effectiveDate: value?.effectiveDate || '',
  companyName: value?.companyName || '',
  title: value?.title || '',
  sections: Array.isArray(value?.sections) ? value!.sections : [],
});

const normalizeSubsection = (subsection: Partial<PrivacyPolicySubsection>): PrivacyPolicySubsection => ({
  number: subsection.number || '',
  title: subsection.title || '',
  bodyHtml: subsection.bodyHtml || '',
});

const normalizeSection = (section: Partial<PrivacyPolicySection>): PrivacyPolicySection => ({
  id: section.id || '',
  number: section.number || '',
  title: section.title || '',
  expandable: Boolean(section.expandable),
  bodyHtml: section.bodyHtml || '',
  subsections: Array.isArray(section.subsections) ? section.subsections.map(normalizeSubsection) : [],
});

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor,
  pushLeft,
  policyContent,
  ...props
}) => {
  const outerClasses = classNames(topOuterDivider && 'has-top-divider', bottomOuterDivider && 'has-bottom-divider');

  const innerClasses = classNames(
    'features-tiles-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider',
  );

  const currentEntry = policyContent?.[0];
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'lt'>('en');
  const currentDocument = normalizeDocument(currentLanguage === 'en' ? currentEntry?.policyEn : currentEntry?.policyLt);
  const sections = currentDocument.sections.map(normalizeSection);

  if (!currentDocument.title && !sections.length) {
    return null;
  }

  return (
    <section
      id="privacy-policy-section"
      {...props}
      className={classNames('privacy-policy section', className, outerClasses)}
    >
      <div className={innerClasses}>
        <div className="container">
          <div className="privacy-policy__controls">
            <Button
              className={classNames('privacy-policy__language-button', currentLanguage === 'en' && 'is-active')}
              color="primary"
              onClick={() => setCurrentLanguage('en')}
              tag={'a'}
            >
              EN
            </Button>
            <Button
              className={classNames('privacy-policy__language-button', currentLanguage === 'lt' && 'is-active')}
              color="primary"
              onClick={() => setCurrentLanguage('lt')}
              tag={'a'}
            >
              LT
            </Button>
          </div>

          <div className="privacy-policy__header">
            {currentDocument.effectiveDate && (
              <p className="privacy-policy__effective-date">{currentDocument.effectiveDate}</p>
            )}
            <p className="privacy-policy__company-name">{currentDocument.companyName}</p>
            <h1 className="privacy-policy__title">{currentDocument.title}</h1>
          </div>

          <div className="privacy-policy__content">
            {sections.map(section => {
              if (section.expandable) {
                return (
                  <details className="privacy-policy__accordion-item" key={section.id}>
                    <summary className="privacy-policy__accordion-summary">
                      <span className="privacy-policy__accordion-title">
                        <span className="privacy-policy__section-number">{section.number}.</span> {section.title}
                      </span>
                      <span aria-hidden="true" className="privacy-policy__accordion-icon"></span>
                    </summary>
                    <div className="privacy-policy__accordion-body">
                      {section.bodyHtml && renderPolicyBody(section.bodyHtml, 'privacy-policy__section-body')}
                      {section.subsections.map(subsection => (
                        <section className="privacy-policy__subsection" key={subsection.number}>
                          <div className="privacy-policy__subsection-title">
                            <span className="privacy-policy__subsection-number">{subsection.number}</span>{' '}
                            {subsection.title}
                          </div>
                          {renderPolicyBody(subsection.bodyHtml, 'privacy-policy__section-body')}
                        </section>
                      ))}
                    </div>
                  </details>
                );
              }

              return (
                <article className="privacy-policy__intro-section" key={section.id}>
                  <div className="privacy-policy__section-title">
                    <span className="privacy-policy__section-number">{section.number}.</span> {section.title}
                  </div>
                  {renderPolicyBody(section.bodyHtml, 'privacy-policy__section-body')}
                  {section.subsections.map(subsection => (
                    <section className="privacy-policy__subsection" key={subsection.number}>
                      <div className="privacy-policy__subsection-title">
                        <span className="privacy-policy__subsection-number">{subsection.number}</span>{' '}
                        {subsection.title}
                      </div>
                      {renderPolicyBody(subsection.bodyHtml, 'privacy-policy__section-body')}
                    </section>
                  ))}
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

PrivacyPolicy.defaultProps = defaultProps;

export default PrivacyPolicy;
